import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger
} from '@bliks-io/ui';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import {
  CALCULATOR_MODULES,
  CALCULATOR_PRICES,
  DEFAULT_CALCULATOR_INPUT,
  MODULES_WITHOUT_SEATS,
  calculateLicenseCost,
  type CalculatorInput,
  type CalculatorModule
} from './calculateLicenseCost';
import { QuantityField, ToggleRow } from './CalculatorFields';
import { PartnerSalesTabsNav } from '@/pages/partner/components/PartnerSalesTabsNav';

const LOCALE_TAGS: Record<string, string> = { de: 'de-DE', en: 'en-GB', fr: 'fr-FR', hu: 'hu-HU' };

function useEuro() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const format = new Intl.NumberFormat(LOCALE_TAGS[i18n.language.slice(0, 2)] ?? 'de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return (value: number) => format.format(value);
  }, [i18n.language]);
}

export function CostCalculatorPage() {
  const { t } = useTranslation();
  const euro = useEuro();
  const [input, setInput] = useState<CalculatorInput>(DEFAULT_CALCULATOR_INPUT);
  const result = calculateLicenseCost(input);

  const set = <K extends keyof CalculatorInput>(key: K, value: CalculatorInput[K]) =>
    setInput((current) => ({ ...current, [key]: value }));
  const setModule = (
    module: CalculatorModule,
    patch: Partial<CalculatorInput['modules'][CalculatorModule]>
  ) =>
    setInput((current) => ({
      ...current,
      modules: { ...current.modules, [module]: { ...current.modules[module], ...patch } }
    }));

  const discountPercent = Math.round(CALCULATOR_PRICES.core.yearlyDiscount * 100);

  return (
    <div className="flex h-full flex-col">
      <PartnerSalesTabsNav />
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto p-6">
        <PartnerPageHeader
          title={t('partner.calculator.title')}
          subtitle={t('partner.calculator.subtitle')}
        />
        <p className="text-xs text-muted-foreground">{t('partner.calculator.temporaryHint')}</p>

        <div className="grid items-start gap-6 lg:grid-cols-2 xl:grid-cols-[1fr_1fr_22rem]">
          <div className="flex flex-col gap-6">
            <Tabs
              value={input.yearly ? 'yearly' : 'monthly'}
              onValueChange={(value) => set('yearly', value === 'yearly')}
            >
              <TabsList>
                <TabsTrigger value="monthly">{t('partner.calculator.monthly')}</TabsTrigger>
                <TabsTrigger value="yearly">
                  {t('partner.calculator.yearly')}
                  <Badge variant="secondary" className="ml-2">
                    −{discountPercent}%
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Card>
              <CardHeader>
                <CardTitle>{t('partner.calculator.coreTitle')}</CardTitle>
                <CardDescription>{t('partner.calculator.coreHint')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4">
                  <QuantityField
                    label={t('partner.calculator.users')}
                    hint={t('partner.calculator.usersSub')}
                    info={t('partner.calculator.roleInfo.users')}
                    value={input.users}
                    min={1}
                    max={200000}
                    onChange={(value) => set('users', value)}
                  />
                  <QuantityField
                    label={t('partner.calculator.sysAdmins')}
                    info={t('partner.calculator.roleInfo.sysAdmins')}
                    value={input.sysAdmins}
                    min={1}
                    max={500}
                    onChange={(value) => set('sysAdmins', value)}
                  />
                  <QuantityField
                    label={t('partner.calculator.orgManagers')}
                    info={t('partner.calculator.roleInfo.orgManagers')}
                    value={input.orgManagers}
                    min={1}
                    max={500}
                    onChange={(value) => set('orgManagers', value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle>{t('partner.calculator.aiTitle')}</CardTitle>
                    <CardDescription>{t('partner.calculator.aiHint')}</CardDescription>
                  </div>
                  <Switch
                    checked={input.aiEnablement}
                    onCheckedChange={(checked) => set('aiEnablement', checked)}
                    aria-label={t('partner.calculator.aiTitle')}
                  />
                </div>
              </CardHeader>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('partner.calculator.modulesTitle')}</CardTitle>
              <CardDescription>{t('partner.calculator.modulesHint')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {CALCULATOR_MODULES.map((module) => (
                  <ToggleRow
                    key={module}
                    label={t(`partner.calculator.moduleNames.${module}`)}
                    info={t(`partner.calculator.moduleInfo.${module}`)}
                    checked={input.modules[module].active}
                    onCheckedChange={(active) => setModule(module, { active })}
                    price={euro(result.moduleYearly[module] / 12)}
                  >
                    {!MODULES_WITHOUT_SEATS.includes(module) && (
                      <QuantityField
                        label={t('partner.calculator.seats')}
                        value={input.modules[module].extraSeats}
                        min={0}
                        max={999}
                        onChange={(extraSeats) => setModule(module, { extraSeats })}
                      />
                    )}
                  </ToggleRow>
                ))}
                <ToggleRow
                  label={t('partner.calculator.moduleNames.isoCheck')}
                  info={t('partner.calculator.moduleInfo.isoCheck')}
                  checked={input.isoCheck.active}
                  onCheckedChange={(active) => set('isoCheck', { ...input.isoCheck, active })}
                  price={`${euro(result.isoYearly)}${t('partner.calculator.perContractYear')}`}
                  meta={t('partner.calculator.queries', { count: result.isoQueries })}
                >
                  <QuantityField
                    label={t('partner.calculator.packages')}
                    value={input.isoCheck.extraPackages}
                    min={0}
                    max={999}
                    onChange={(extraPackages) =>
                      set('isoCheck', { ...input.isoCheck, extraPackages })
                    }
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('partner.calculator.isoHint')}
                  </p>
                </ToggleRow>
              </div>
            </CardContent>
          </Card>

          <PriceSummary result={result} yearly={input.yearly} euro={euro} />
        </div>
      </div>
    </div>
  );
}

function PriceSummary({
  result,
  yearly,
  euro
}: {
  result: ReturnType<typeof calculateLicenseCost>;
  yearly: boolean;
  euro: (value: number) => string;
}) {
  const { t } = useTranslation();
  const rows = [
    { label: t('partner.calculator.coreTitle'), value: euro(result.coreYearly / 12) },
    { label: t('partner.calculator.modulesTitle'), value: euro(result.modulesYearly / 12) },
    ...(result.aiYearly > 0
      ? [{ label: t('partner.calculator.aiTitle'), value: euro(result.aiYearly / 12) }]
      : []),
    ...(result.isoYearly > 0
      ? [
          {
            label: t('partner.calculator.moduleNames.isoCheck'),
            value: `${euro(result.isoYearly)}${t('partner.calculator.perContractYear')}`
          }
        ]
      : [])
  ];

  return (
    <Card className="lg:sticky lg:top-6">
      <CardHeader>
        <CardDescription>{t('partner.calculator.summary')}</CardDescription>
        <p className="text-4xl font-bold tracking-tight tabular-nums" aria-live="polite">
          {euro(result.monthlyExcludingIso)}
        </p>
        <CardDescription>
          {t('partner.calculator.perMonth')} · {euro(result.totalYearly)}{' '}
          {t('partner.calculator.perYear')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {yearly && result.yearlySaving > 0 && (
            <div className="flex justify-between gap-3 rounded-md bg-muted p-3 text-sm">
              <span className="text-muted-foreground">{t('partner.calculator.saving')}</span>
              <span className="font-semibold tabular-nums">−{euro(result.yearlySaving)}</span>
            </div>
          )}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('partner.calculator.breakdown')}
            </p>
            {rows.map((row) => (
              <div key={row.label} className="flex justify-between gap-3 py-1 text-sm tabular-nums">
                <span className="text-muted-foreground">{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('partner.calculator.vat')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
